import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Download } from "lucide-react";
import { Badge, PIPELINE, STATUS_STYLE, KIND_BADGE, fmtDate } from "./recruitLabels";

export const CandidatesTab = ({ offers, offerFilter, onOfferFilter, onOpen, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/recrutement/candidates", { params: { offer_id: offerFilter, kind, q: q || undefined } });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [offerFilter, kind, refreshKey]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/recrutement/candidates/export.csv", { params: { offer_id: offerFilter, kind, q: q || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url;
      a.download = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "candidatures.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success(`Export CSV : ${r.headers["x-row-count"] ?? items.length} ligne(s)`);
    } catch (e) { toast.error("Export impossible"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="candidates-tab">
      <div className="bg-white border border-[#DEE2E6] p-3 flex flex-wrap items-center gap-2">
        <select value={offerFilter} onChange={(e) => onOfferFilter(e.target.value)} data-testid="cand-filter-offer" className="h-8 border border-[#DEE2E6] bg-white px-2 text-xs max-w-xs">
          <option value="all">Toutes les offres</option>
          <option value="spontaneous">Candidatures spontanées</option>
          {offers.map((o) => <option key={o.id} value={o.id}>{o.reference} — {o.title}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cand-filter-kind" className="h-8 border border-[#DEE2E6] bg-white px-2 text-xs">
          <option value="all">Emplois + BP</option><option value="emploi">Emplois</option><option value="business_partner">Business Partners</option>
        </select>
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, email, référence, SIRET…" className="h-8 w-56 text-xs" data-testid="cand-search" />
          <button type="submit" className="h-8 px-2 border border-[#DEE2E6] bg-white hover:border-[#002FA7]" data-testid="cand-search-btn"><Search className="h-3.5 w-3.5" /></button>
        </form>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting} className="h-8 text-[10px] uppercase tracking-wider" data-testid="cand-export-csv"><Download className="h-3.5 w-3.5 mr-1" />{exporting ? "Export…" : "Exporter CSV"}</Button>
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]" data-testid="cand-count">{items.length} candidat(s)</div>
      </div>

      <div className="grid md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="cand-kanban">
        {PIPELINE.map((st) => {
          const col = items.filter((c) => c.status === st);
          const s = STATUS_STYLE[st];
          return (
            <div key={st} className={`bg-white border border-[#DEE2E6] border-t-4 ${s.col} min-h-[200px]`} data-testid={`kanban-col-${st}`}>
              <div className="px-3 py-2 flex items-center justify-between border-b border-[#DEE2E6]">
                <span className="text-[10px] uppercase tracking-wider font-bold">{s.label}</span>
                <span className="text-[10px] font-bold text-[#868E96]" data-testid={`kanban-count-${st}`}>{col.length}</span>
              </div>
              <div className="p-2 space-y-2">
                {col.map((c) => (
                  <button key={c.id} type="button" onClick={() => onOpen(c.id)} data-testid={`cand-card-${c.id}`}
                          className="w-full text-left border border-[#DEE2E6] hover:border-[#002FA7] bg-[#F8F9FA] p-2.5 transition-colors">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[9px] font-mono text-[#868E96]">{c.reference}</span>
                      <span className="flex items-center gap-1">{c.spontaneous && <span className="text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 bg-[#0A0A0A] text-white" data-testid={`cand-spontaneous-${c.id}`}>Spontanée</span>}<Badge cfg={KIND_BADGE[c.kind]} /></span>
                    </div>
                    <div className="text-sm font-semibold mt-1 truncate">{c.prenom} {c.nom}</div>
                    <div className="text-[10px] text-[#495057] truncate">{c.offer_title}</div>
                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#868E96]">
                      <span>{fmtDate(c.created_at)}</span>
                      {c.rating != null && <span className="font-bold text-[#F59E0B]">★ {c.rating}</span>}
                    </div>
                  </button>
                ))}
                {col.length === 0 && <div className="text-[10px] text-[#868E96] text-center py-6">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
